/**
 * D6 (RET-1877) — persists alert acknowledgments in localStorage, per
 * workspace AND user, so a dismissed P3 stays dismissed across reloads and an
 * acknowledged P1 does not re-inflate the bell badge every session. Follows
 * the `NewsCard.storage.ts` pattern: scoped key builder, versioned payload,
 * parse + normalize with prune-on-invalid, safe(Get/Set/Remove) that never
 * throws.
 *
 * What is stored is the alert KEY (`<alert>:<workspace>[:<period>]`, built by
 * `buildAlertKey`), mapped to the acknowledgment timestamp. The key already
 * carries the occurrence segment, so period expiry needs no logic here: next
 * billing period produces a NEW key, which is simply not in the map (R13b).
 * Old-period keys linger as dead entries until the age GC below collects
 * them.
 *
 * Every mutation reads fresh from storage immediately before writing — never
 * from a snapshot the caller captured earlier — so a parked tab holding stale
 * in-memory state cannot resurrect an acknowledgment pruned in another tab,
 * nor lose one granted there (the NewsCard plan-decision-3 rule).
 *
 * This is the LOCAL layer only. Server-side persistence is still the open Q1
 * seam in `AlertCenter.service.ts`; nothing here claims durability beyond
 * this browser.
 */

const KEY_PREFIX = 'alert_center_acks_';

/**
 * Entries older than this are dropped on read. 60 days comfortably outlives
 * every acknowledgment's useful life (billing periods are monthly; episodic
 * alerts prune at episode end) while keeping abandoned keys — retired
 * declarations, old periods, workspaces the user left — from accumulating
 * forever.
 */
export const ACKNOWLEDGMENT_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;

/** Acknowledged alert key → epoch ms of the acknowledgment. */
export type tAcknowledgmentStorageState = {
  v: 1;
  acks: Record<string, number>;
};

export type tAcknowledgmentScope = {
  workspaceId: string;
  userId: string;
};

/**
 * Scoped by BOTH workspace and user: roles see different alert lists, so a
 * shared key would let an owner's acknowledgment silence an alert for a
 * seller on the same browser (the NewsCard precedent).
 */
export function getAcknowledgmentStorageKey(scope: tAcknowledgmentScope): string {
  return `${KEY_PREFIX}${scope.workspaceId}_${scope.userId}`;
}

function freshState(): tAcknowledgmentStorageState {
  return { v: 1, acks: {} };
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Access denied (Safari private mode can throw on read too) — treat
    // exactly like "nothing stored".
    return null;
  }
}

function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    // QuotaExceededError or access denied — acknowledgments degrade to the
    // caller's in-memory state for this session; dropping the write is safer
    // than crashing the provider that wraps every private page.
    return false;
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* best-effort cleanup */
  }
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Keeps only entries that are shaped right AND young enough. Validating the
 * timestamp is what makes a foreign payload harmless; the age cut is the GC.
 */
function normalizeAcks(raw: unknown, now: number): Record<string, number> {
  if (!raw || typeof raw !== 'object') return {};
  const result: Record<string, number> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, timestamp]) => {
    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return;
    if (now - timestamp > ACKNOWLEDGMENT_MAX_AGE_MS) return;
    result[key] = timestamp;
  });
  return result;
}

function readState(scope: tAcknowledgmentScope): tAcknowledgmentStorageState {
  if (!scope.workspaceId || !scope.userId) return freshState();

  const key = getAcknowledgmentStorageKey(scope);
  const raw = safeGetItem(key);
  if (raw === null) return freshState();

  const parsed = parseJson(raw);
  if (!parsed || typeof parsed !== 'object') {
    // Corrupted JSON — clean up so future reads stop re-parsing garbage.
    safeRemoveItem(key);
    return freshState();
  }

  const obj = parsed as Record<string, unknown>;
  if (obj.v !== 1) {
    // Unknown/foreign/future shape — must not be trusted; treat as fresh.
    safeRemoveItem(key);
    return freshState();
  }

  return { v: 1, acks: normalizeAcks(obj.acks, Date.now()) };
}

/**
 * The acknowledgments with their instant (epoch ms), expired entries already
 * filtered out. Pure read: the GC'd result is NOT written back here (reads
 * happen during render via the provider's memo; writes belong to mutations,
 * which persist the normalized state as a side effect of their own change).
 *
 * AGI-891 (U6): the feed's history shows when a notice was seen. The instant
 * was always stored with the key, only the keys were read.
 */
export function readAcknowledgments(
  scope: tAcknowledgmentScope,
): Record<string, number> {
  return readState(scope).acks;
}

/** The acknowledged keys: `readAcknowledgments` without the instants. */
export function readAcknowledgedKeys(scope: tAcknowledgmentScope): string[] {
  return Object.keys(readAcknowledgments(scope));
}

/**
 * Grants one acknowledgment. Read-fresh-before-write, then returns the full
 * resulting key list — which is the intended state even when the write did
 * not land (quota/private mode): the caller keeps it in memory so the acuse
 * still works for this session and simply is not durable.
 */
export function acknowledgeKeyInStorage(
  scope: tAcknowledgmentScope,
  alertKey: string,
): string[] {
  const state = readState(scope);
  state.acks[alertKey] = Date.now();
  if (scope.workspaceId && scope.userId) {
    safeSetItem(getAcknowledgmentStorageKey(scope), JSON.stringify(state));
  }
  return Object.keys(state.acks);
}

/**
 * Removes acknowledgments whose episode verifiably ended (the provider's
 * pruner decides which — see `selectLiveEpisodeKeys`). Read-fresh-before-
 * write for the same cross-tab reason as above.
 */
export function pruneKeysInStorage(
  scope: tAcknowledgmentScope,
  endedKeys: string[],
): string[] {
  const state = readState(scope);
  endedKeys.forEach((key) => {
    delete state.acks[key];
  });
  if (scope.workspaceId && scope.userId) {
    safeSetItem(getAcknowledgmentStorageKey(scope), JSON.stringify(state));
  }
  return Object.keys(state.acks);
}
